import {
  ADDRESS,
  AUTH_SUCCESS,
  BOUGHT_IN_STORE,
  BROWSE,
  BUYLIST,
  CHECKOUT,
  CMS,
  COLLECTION,
  CUSTOM_AISLE,
  DELIVERY,
  ENTERTAINING,
  FAVOURITES,
  FORGOT_PASSWORD,
  HOME,
  INTERSTITIALS,
  LISTS,
  LOG_OUT,
  MEAL_DEALS,
  MORE_OF_WHAT_YOU_LOVE,
  MULTI_SEARCH,
  MY_ORDERS,
  MY_WAITROSE,
  OFFERS,
  ORDER_CONFIRMATION,
  PAYMENT_CONFIRMATION,
  QUICK_SHOP,
  RECIPES,
  REGISTRATION,
  RESOLVE_PAYMENT,
  SEARCH,
  SERVICE_SELECTION,
  SHOP_BY_LIST,
  SHOP_FROM_ORDER,
  SHOPPING_LISTS,
  TROLLEY,
  UNKNOWN,
  VIEW_ORDER,
} from 'constants/pageTypes';
import urls, { cmsUrls } from 'constants/urls';
import { RESCUE_POD_BANNER } from 'constants/experience-fragments';
import { rootPath } from 'utils/root-path';

import { isCheckoutFetchRequired } from 'redux/fetchers/is-checkout-fetch-required';
import { all } from 'route-data/all';
import { chain } from 'route-data/chain';
import { conditional } from 'route-data/conditional';
import type { Fetcher, PreloadOnlyFetcher } from 'route-data/fetcher-types';
import { lazyFetcher } from 'route-data/lazy-fetcher';
import { redirectIfLoggedOut } from 'route-data/redirect-if-logged-out';
import { isNotEmbeddedInWebView } from 'utils/webview';
import { exceptWhenLoggedOut } from './except-when-logged-out';
import { withFeatureFlag } from './with-feature-flag';

interface Route {
  analyticsName?: string;
  getAnalyticsName?: (pathname: string) => string;
  clientFetch?: PreloadOnlyFetcher | Fetcher;
  exact?: boolean;
  fetch?: PreloadOnlyFetcher | Fetcher;
  noCommonClientFetch?: boolean;
  noCommonFetch?: boolean;
  pageType?: string;
  path: string;
  preload: () => void;
}

const commonClientFetch = lazyFetcher(
  () => import('route-data/common-client-fetch'),
  'commonClientFetch',
);

const accountsFetcher = lazyFetcher(() => import('redux/fetchers/accounts'), 'accountsFetcher');
const addressByIdFetcher = lazyFetcher(
  () => import('redux/fetchers/address'),
  'addressByIdFetcher',
);
const linkedMembershipFetcher = lazyFetcher(
  () => import('redux/fetchers/linked-membership'),
  'linkedMembershipFetcher',
);
const addressesFetcher = lazyFetcher(
  () => import('redux/fetchers/address/addressesFetcher'),
  'addressesFetcher',
);
const allFavouritesFetcherLoggedIn = lazyFetcher(
  () => import('redux/fetchers/favourites-all'),
  'allFavouritesFetcherLoggedIn',
);
const glpFetcher = lazyFetcher(() => import('redux/fetchers/groceriesLandingPage'), 'glpFetcher');
const checkoutExperienceFragmentFetcher = lazyFetcher(
  () => import('redux/fetchers/checkout-experience-fragment-fetcher'),
  'checkoutExperienceFragmentFetcher',
);
const checkoutRouteLoaded = lazyFetcher(
  () => import('redux/fetchers/checkout-route-loaded'),
  'checkoutRouteLoaded',
);

const citrusAdsProductFetcher = lazyFetcher(
  () => import('redux/fetchers/citrusads-buylist-page'),
  'citrusAdsProductFetcher',
);
const cmsExperienceFragment = (
  ...args: Parameters<typeof import('redux/fetchers/experience-fragment').default>
) =>
  lazyFetcher(
    () => import('redux/fetchers/experience-fragment'),
    'default',
    fetcher => fetcher(...args),
  );
const cmsPageFetcher = lazyFetcher(() => import('redux/fetchers/cms-page'), 'cmsPageFetcher');
const recipesGridFetcher = lazyFetcher(
  () => import('redux/fetchers/recipes-grid-fetcher'),
  'recipesGridFetcher',
);
const mealPlannerFetcher = lazyFetcher(() => import('redux/fetchers/meal-planner'));
const mealPlannerFiltersFetcher = lazyFetcher(
  () => import('redux/fetchers/meal-planner'),
  'fetchMealPlannerFilters',
);
const getProductsForRecipesFetcher = lazyFetcher(
  () => import('redux/fetchers/meal-planner'),
  'getProductsForRecipes',
);

const getRecipesForMealPlannerFetcher = lazyFetcher(
  () => import('redux/fetchers/meal-planner'),
  'getRecipesForMealPlanner',
);

const componentDataFetcher = lazyFetcher(
  () => import('redux/fetchers/component-data'),
  'componentDataFetcher',
);
const createAisleFetcher = (
  ...args: Parameters<typeof import('redux/fetchers/custom-aisle').createAisleFetcher>
) =>
  lazyFetcher(
    () => import('redux/fetchers/custom-aisle'),
    'createAisleFetcher',
    fetcher => fetcher(...args),
  );
const customerSlotFetcher = lazyFetcher(
  () => import('redux/fetchers/customer-slot-fetcher'),
  'customerSlotFetcher',
);
const editAdminAddressFetcher = lazyFetcher(
  () => import('redux/fetchers/address'),
  'editAdminAddressFetcher',
);
const fetchCustomerOrderStats = lazyFetcher(
  () => import('redux/modules/orders/actions/fetch-customer-order-stats'),
  'fetchCustomerOrderStats',
);
const footerFetcher = lazyFetcher(() => import('redux/fetchers/footer'), 'footerFetcher');
const incentivesFetcher = (
  ...args: Parameters<typeof import('redux/fetchers/incentives').incentivesFetcher>
) =>
  lazyFetcher(
    () => import('redux/fetchers/incentives'),
    'incentivesFetcher',
    fetcher => fetcher(...args),
  );
const interstitialFetcher = lazyFetcher(
  () => import('redux/fetchers/interstitial'),
  'interstitialFetcher',
);
const interstitialsFetcher = lazyFetcher(
  () => import('redux/fetchers/interstitials'),
  'interstitialsFetcher',
);
const jotterFetcher = lazyFetcher(() => import('redux/fetchers/jotter'), 'jotterFetcher');
const membershipStatusFetcher = lazyFetcher(
  () => import('redux/fetchers/membership-status'),
  'membershipStatusFetcher',
);
const menuFetcher = lazyFetcher(() => import('redux/fetchers/menu'), 'menuFetcher');
const multiSearchFetcher = lazyFetcher(
  () => import('redux/fetchers/multi-search'),
  'multiSearchFetcher',
);
const multiSearchFetcherLoading = lazyFetcher(
  () => import('redux/fetchers/multi-search-loading'),
  'multiSearchFetcherLoading',
);
const myWaitroseFetcher = lazyFetcher(
  () => import('redux/fetchers/my-waitrose'),
  'myWaitroseFetcher',
);
const navigationFetcher = lazyFetcher(
  () => import('redux/fetchers/navigation'),
  'navigationFetcher',
);
const newJoinerAddressesFetcher = lazyFetcher(
  () => import('redux/fetchers/address'),
  'newJoinerAddressesFetcher',
);
const offersFetcher = lazyFetcher(() => import('redux/fetchers/offers'), 'offersFetcher');
const orderInitialiser = lazyFetcher(() => import('redux/fetchers/order'), 'orderInitialiser');
const orderByURLFetcher = lazyFetcher(() => import('redux/fetchers/order'), 'orderByURLFetcher');
const paymentCardsFetcher = lazyFetcher(
  () => import('redux/fetchers/payment-cards'),
  'paymentCardsFetcher',
);

const paymentConfirmationFetcher = lazyFetcher(
  () => import('redux/fetchers/payment-confirmation'),
  'paymentConfirmationFetcher',
);

const recipeFetcher = lazyFetcher(() => import('redux/fetchers/recipes'), 'recipeFetcher');
const recipeProductFetcher = lazyFetcher(
  () => import('redux/fetchers/recipes'),
  'recipeProductFetcher',
);
const searchFetcher = lazyFetcher(() => import('redux/fetchers/search'), 'searchFetcher');
const trolleyFetcher = lazyFetcher(() => import('redux/fetchers/trolley'), 'trolleyFetcher');

const loggedInCustomerDataFetcher = lazyFetcher(
  () => import('redux/fetchers/logged-in-customer-data'),
);

const cmsPaths = cmsUrls.map(path => ({
  path,
  pageType: CMS,
  exact: !path.includes('/:path/*'),
  fetch: lazyFetcher(() => import('components/CmsPage'), 'cmsPageParallelFetcher'),
  noCommonFetch: true,
  noCommonClientFetch: false,
  clientFetch: undefined, // TS seems to shout if this isn't here...
}));

// This is a switch-like table; only the first matching route will be used.
// Each route object can have props 'path', 'exact' and 'strict', as per <Route/>.
//
// If a path matches, the 'fetch' function will be invoked, passing { location, match },
// and the action returned will be dispatched. If a 'clientFetch' fetcher is set, it too will
// be invoked, but only on the client-side.
//
// On the client-side, used by <DataProvider/> and pre-render-actions via oneTimeFetch to fetch data based on location
// On the server-side, used by oneTimeFetch() to fetch data, based on express request object
const paths = [
  ...cmsPaths,
  {
    path: rootPath('/shop/browse/offers/highlights/:offerType/*'),
    analyticsName: 'Offers',
    pageType: BROWSE,
    fetch: all(offersFetcher, allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/shop/browse/offers/*'),
    analyticsName: 'Offers',
    pageType: BROWSE,
    fetch: all(offersFetcher, allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/shop/browse/groceries'),
    analyticsName: 'On Groceries Homepage',
    pageType: BROWSE,
    exact: true,
    fetch: all(
      chain(menuFetcher, glpFetcher, allFavouritesFetcherLoggedIn),
      footerFetcher,
      cmsExperienceFragment(RESCUE_POD_BANNER.key, { numberOfRetries: 2, cache: false }),
    ),
    clientFetch: componentDataFetcher,
  },
  { path: rootPath('/shop/featured/entertaining'), pageType: ENTERTAINING, exact: true }, // Redirects on render
  {
    path: rootPath('/shop/featured/groceries'),
    analyticsName: 'Branded Hub',
    pageType: CUSTOM_AISLE,
    exact: true,
  }, // Redirects on render
  {
    path: rootPath('/shop/featured/offers'),
    analyticsName: 'Custom Aisle',
    pageType: OFFERS,
    exact: true,
  }, // Redirects on render
  {
    path: rootPath('/shop/featured/entertaining/*'),
    pageType: ENTERTAINING,
    fetch: all(createAisleFetcher('entertaining'), allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/shop/featured/groceries/*'),
    analyticsName: 'Branded Hub',
    pageType: CUSTOM_AISLE,
    fetch: all(createAisleFetcher('groceries'), allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: `${urls.mealDealPromoPath}/:mealDealId`,
    analyticsName: 'Meal Deals',
    pageType: MEAL_DEALS,
    fetch: lazyFetcher(() => import('redux/fetchers/meal-deals-page'), 'mealDealsPageFetcher'),
    noCommonFetch: true,
  },
  {
    path: rootPath('/shop/featured/offers/*'),
    analyticsName: 'Custom Aisle',
    pageType: OFFERS,
    fetch: all(createAisleFetcher('offers'), allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/shop/multi-search'),
    analyticsName: 'Multi Search',
    pageType: MULTI_SEARCH,
    fetch: all(allFavouritesFetcherLoggedIn, multiSearchFetcherLoading, navigationFetcher),
    clientFetch: multiSearchFetcher,
  },
  {
    path: rootPath('/shop/offers/:offerId'),
    analyticsName: 'Offers',
    pageType: OFFERS,
    fetch: lazyFetcher(
      () => import('redux/fetchers/offer-details-page'),
      'offerDetailsPageFetcher',
    ),
    noCommonFetch: true,
  },
  {
    path: rootPath('/shop/search'),
    analyticsName: 'Normal Search',
    pageType: SEARCH,
    fetch: all(
      searchFetcher,
      allFavouritesFetcherLoggedIn,
      navigationFetcher,
      cmsExperienceFragment(RESCUE_POD_BANNER.key, { numberOfRetries: 2, cache: false }),
    ),
    clientFetch: componentDataFetcher,
  },
  {
    path: urls.favourites,
    analyticsName: 'Favourites',
    pageType: FAVOURITES,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('redux/fetchers/favourites'), 'favouritesFetcher'),
    // Current experiments on favourites require the client-side Monetate API
    // call to resolve before calling Favourites Experience, so we disable
    // common client-side fetching and manually chain it to the
    // `favouritesProductsFetcher` inside of the `favouritesFetcher` itself.
    noCommonClientFetch: true,
  },
  {
    path: urls.boughtInStore,
    analyticsName: 'Bought In-Store',
    pageType: BOUGHT_IN_STORE,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('redux/fetchers/bought-in-store'), 'boughtInStoreFetcher'),
  },
  {
    path: urls.quickShop,
    analyticsName: 'Quick Shop',
    pageType: QUICK_SHOP,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('redux/fetchers/quick-shop-page'), 'quickShopPageFetcher'),
  },
  {
    path: urls.moreOfWhatYouLove,
    analyticsName: 'More of what you love',
    pageType: MORE_OF_WHAT_YOU_LOVE,
    noCommonFetch: true,
    fetch: lazyFetcher(
      () => import('redux/fetchers/more-of-what-you-love-page'),
      'moreOfWhatYouLovePageFetcher',
    ),
  },
  {
    path: urls.lists,
    analyticsName: 'Lists',
    pageType: LISTS,
    noCommonFetch: true,
    fetch: lazyFetcher(
      () => import('redux/fetchers/shopping-lists-page'),
      'shoppingListsPageFetcher',
    ),
  },
  {
    path: urls.shoppingLists,
    analyticsName: 'Custom Lists',
    pageType: SHOPPING_LISTS,
    noCommonFetch: true,
    fetch: lazyFetcher(
      () => import('redux/fetchers/shopping-list-page'),
      'shoppingListPageFetcher',
    ),
  },
  {
    path: urls.shopByList,
    analyticsName: 'Match My List',
    pageType: SHOP_BY_LIST,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('redux/fetchers/shop-by-list-page'), 'shopByListPageFetcher'),
  },
  {
    path: urls.shopByListUpload,
    pageType: SHOP_BY_LIST,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: footerFetcher,
    clientFetch: lazyFetcher(
      () => import('redux/fetchers/shop-by-list-upload-page'),
      'shopByListUploadPageClientFetcher',
    ),
  },
  {
    path: urls.shopByListSuggestions,
    analyticsName: 'Match My List',
    pageType: SHOP_BY_LIST,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('redux/fetchers/shop-by-list-page'), 'shopByListPageFetcher'),
  },
  {
    path: rootPath('/my-waitrose/link-card'),
    analyticsName: 'myWaitrose',
    pageType: MY_WAITROSE,
    fetch: all(newJoinerAddressesFetcher, navigationFetcher, linkedMembershipFetcher),
    exact: true,
  },
  {
    path: rootPath('/customer/address'),
    pageType: ADDRESS,
    fetch: all(editAdminAddressFetcher),
  },
  {
    path: rootPath('/beforeYouGo/:interstitialId'),
    getAnalyticsName: (pathname: string) => {
      if (pathname.includes('_missed-offers')) return 'Interstitials: Missed offers';
      if (pathname.includes('_managers-special')) return 'Interstitials: Managers special';
      if (pathname.includes('_beforeYouGo')) return 'Interstitials: Before You Go';
      return 'Interstitials: Missed favourites';
    },
    pageType: INTERSTITIALS,
    fetch: all(interstitialFetcher, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/beforeYouGo'),
    pageType: INTERSTITIALS,
    fetch: all(interstitialsFetcher, allFavouritesFetcherLoggedIn, navigationFetcher),
    clientFetch: componentDataFetcher,
  },
  {
    path: rootPath('/my-waitrose/order-replacement-card/address'),
    analyticsName: 'myWaitrose',
    pageType: MY_WAITROSE,
    fetch: all(addressesFetcher, myWaitroseFetcher, navigationFetcher, linkedMembershipFetcher),
    exact: true,
  },
  {
    path: rootPath('/my-waitrose/link-card/welcome'),
    analyticsName: 'myWaitrose',
    pageType: MY_WAITROSE,
    fetch: all(
      addressesFetcher,
      membershipStatusFetcher,
      navigationFetcher,
      linkedMembershipFetcher,
    ),
    exact: true,
  },
  {
    path: rootPath('/my-partnership/card-and-details'),
    pageType: MY_WAITROSE,
    fetch: all(accountsFetcher, myWaitroseFetcher, navigationFetcher, linkedMembershipFetcher),
    exact: true,
  },
  {
    path: rootPath('/*/addresses/edit/:addressId'),
    pageType: ADDRESS,
    fetch: all(addressByIdFetcher),
  },
  {
    path: rootPath('/checkout'),
    pageType: CHECKOUT,
    fetch: chain(
      redirectIfLoggedOut(),
      exceptWhenLoggedOut(
        conditional(
          isCheckoutFetchRequired,
          chain(
            all(
              trolleyFetcher,
              accountsFetcher,
              fetchCustomerOrderStats,
              addressesFetcher,
              orderInitialiser,
              paymentCardsFetcher,
              incentivesFetcher({
                ignoreErrors: true,
                timeout: { handleTimeout: true, deadline: 8000, response: 8000 },
              }),
              withFeatureFlag('checkout_excludeExternal', membershipStatusFetcher),
            ),
            checkoutRouteLoaded,
            all(
              checkoutExperienceFragmentFetcher,
              customerSlotFetcher,
              allFavouritesFetcherLoggedIn,
            ),
          ),
        ),
      ),
    ),
    exact: true,
    noCommonFetch: true,
    noCommonClientFetch: true,
  },
  {
    path: rootPath('/resolve-order-payment/:customerOrderId'),
    pageType: RESOLVE_PAYMENT,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: chain(
      redirectIfLoggedOut(),
      exceptWhenLoggedOut(
        chain(
          all(
            lazyFetcher(
              () => import('components/ResolveOrderPayment'),
              'resolveOrderPaymentFetcher',
            ),
            addressesFetcher,
            cmsExperienceFragment('buy/checkout/payment-alert/master', {
              numberOfRetries: 1,
              cache: true,
            }),
          ),
          checkoutRouteLoaded,
        ),
      ),
    ),
  },
  {
    path: rootPath('/payment-confirmation/:customerOrderId'),
    pageType: PAYMENT_CONFIRMATION,
    fetch: all(paymentConfirmationFetcher, navigationFetcher),
  },
  {
    path: rootPath('/order-confirmation/:customerOrderId/*'),
    pageType: ORDER_CONFIRMATION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: all(
      orderByURLFetcher,
      loggedInCustomerDataFetcher,
      conditional(
        isNotEmbeddedInWebView,
        all(
          // Addresses are not necessary in apps as we don't have any way to get to them directly in the webview
          addressesFetcher,
          // Used for multi-search. Not necessary in apps as they don't have the header
          jotterFetcher,
          cmsExperienceFragment('buy/checkout/charity/master', {
            applyColumnTransformation: true,
          }),
          cmsExperienceFragment('buy/checkout/order-confirmation-ads/master', {
            applyColumnTransformation: true,
          }),
          navigationFetcher,
        ),
      ),
    ),
  },
  {
    path: urls.trolleyPage,
    analyticsName: 'Trolley Page',
    pageType: TROLLEY,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/Trolley'), 'trolleyWithRecommendationsFetcher'),
    clientFetch: lazyFetcher(() => import('components/Trolley'), 'trolleyClientFetcher'),
  },
  {
    path: rootPath('/recipe/:recipeId'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    fetch: all(chain(recipeFetcher, recipeProductFetcher), navigationFetcher),
  },
  {
    path: rootPath('/recipes/meal-plans/serves'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: false,
    fetch: all(mealPlannerFiltersFetcher, navigationFetcher),
  },
  {
    path: rootPath('/recipes/meal-plans/how-to'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: false,
    fetch: all(mealPlannerFiltersFetcher, navigationFetcher),
  },
  {
    path: rootPath('/recipes/meal-plans/dietary'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: false,
    fetch: all(mealPlannerFiltersFetcher, navigationFetcher),
  },
  {
    path: rootPath('/recipes/meal-plans/*'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: false,
    fetch: all(navigationFetcher, chain(mealPlannerFetcher, componentDataFetcher)),
    clientFetch: all(chain(getRecipesForMealPlannerFetcher, getProductsForRecipesFetcher)),
  },
  {
    path: rootPath('/recipes/:recipecollection/*'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: false,
    fetch: all(chain(recipesGridFetcher, componentDataFetcher), navigationFetcher),
  },
  {
    path: rootPath('/recipes'),
    analyticsName: 'Recipes',
    pageType: RECIPES,
    exact: true,
    fetch: all(chain(cmsPageFetcher, componentDataFetcher), navigationFetcher),
  },
  {
    path: `${urls.citrusAdsBuylistPage}/:lineNumbers`,
    analyticsName: 'Buylist',
    pageType: BUYLIST,
    fetch: all(citrusAdsProductFetcher, allFavouritesFetcherLoggedIn, navigationFetcher),
  },
  {
    path: rootPath('/registration/*'),
    pageType: REGISTRATION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: footerFetcher,
  },
  {
    path: rootPath('/bookslot/collection/entertaining'),
    pageType: COLLECTION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'fetch'),
    clientFetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'clientFetch'),
  },
  {
    path: rootPath('/bookslot/collection/groceries/*'), // groceries === groceriesandentertaining
    pageType: COLLECTION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'fetch'),
    clientFetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'clientFetch'),
  },
  {
    path: rootPath('/bookslot/delivery/*'),
    pageType: DELIVERY,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'fetch'),
    clientFetch: lazyFetcher(() => import('components/BookSlot/SlotGrid'), 'clientFetch'),
  },
  {
    path: rootPath('/bookslot/collection/selectcollectionservice'),
    pageType: COLLECTION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/BookSlot/ServiceSelectionCollection'), 'fetch'),
    clientFetch: lazyFetcher(
      () => import('components/BookSlot/ServiceSelectionCollection'),
      'clientFetch',
    ),
  },
  {
    path: urls.serviceSelection,
    pageType: SERVICE_SELECTION,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/BookSlot/ServiceSelection'), 'fetch'),
    clientFetch: lazyFetcher(() => import('components/BookSlot/ServiceSelection'), 'clientFetch'),
  },
  {
    path: `${urls.forgotPassword}/*`,
    pageType: FORGOT_PASSWORD,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: footerFetcher,
  },
  {
    path: rootPath('/log-out'),
    pageType: LOG_OUT,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: footerFetcher,
  },
  {
    path: urls.authSuccess,
    pageType: AUTH_SUCCESS,
    noCommonFetch: true,
    noCommonClientFetch: true,
  },
  {
    path: urls.myOrdersPage,
    analyticsName: 'Previous Order',
    pageType: MY_ORDERS,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/MyOrders'), 'ordersFetcher'),
    clientFetch: lazyFetcher(() => import('components/MyOrders'), 'ordersClientFetcher'),
    exact: true,
  },
  {
    path: urls.shopFromPrevious,
    analyticsName: 'Previous Order',
    pageType: SHOP_FROM_ORDER,
    noCommonFetch: true,
    fetch: lazyFetcher(
      () => import('redux/fetchers/shop-from-previous'),
      'shopFromPreviousFetcher',
    ),
  },
  {
    path: urls.shopFromPreviousInterstitial,
    analyticsName: 'Previous Order',
    pageType: SHOP_FROM_ORDER,
    noCommonFetch: true,
    fetch: lazyFetcher(
      () => import('redux/fetchers/shop-from-previous-interstitial'),
      'shopFromPreviousInterstitialFetcher',
    ),
  },
  {
    path: urls.viewOrder,
    analyticsName: 'Previous Order',
    pageType: VIEW_ORDER,
    noCommonFetch: true,
    noCommonClientFetch: true,
    fetch: lazyFetcher(() => import('components/ViewOrder'), 'viewOrderFetcher'),
    clientFetch: lazyFetcher(() => import('components/ViewOrder'), 'viewOrderClientFetcher'),
  },
  {
    path: '/',
    analyticsName: 'On Homepage',
    pageType: HOME,
    exact: true,
    noCommonFetch: true,
    fetch: lazyFetcher(() => import('components/HomePage'), 'homepageFetcher'),
  },
  { path: '/*', pageType: UNKNOWN, fetch: navigationFetcher }, // Catch-all
];

// Fetchers here are invoked for every route
const common = {
  createFetcherChain: (fetchers: unknown) =>
    lazyFetcher(
      () => import('route-data/common-fetch'),
      'createFetcherChain',
      createFetcherChain => createFetcherChain({ fetchersAfterLoggedInCustomerData: fetchers }),
    ),
  clientFetch: commonClientFetch,
};

export const routes: Route[] = paths.map(path => ({
  ...path,
  ...((path.fetch || path.clientFetch) && {
    fetch: path.noCommonFetch ? path.fetch : common.createFetcherChain(path.fetch),
    clientFetch: path.noCommonClientFetch
      ? path.clientFetch
      : all(common.clientFetch, path.clientFetch),
  }),
  preload() {
    if (this.fetch) {
      (this.fetch as PreloadOnlyFetcher)({ preloadOnly: true });
    }
    if (this.clientFetch) {
      (this.clientFetch as PreloadOnlyFetcher)({ preloadOnly: true });
    }
  },
}));
